import { useRef, useState, useEffect } from "react";
import SigPad from "signature_pad";
import { Eraser, Undo2 } from "lucide-react";

export function SignaturePad({ value, onChange, height = 140 }) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const [empty, setEmpty] = useState(!value);

  useEffect(() => {
    const c = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    c.width = c.offsetWidth * ratio;
    c.height = height * ratio;
    c.getContext("2d").scale(ratio, ratio);
    const pad = new SigPad(c, {
      penColor: "#3E2723",
      minWidth: 1.1,
      maxWidth: 2.8,
      velocityFilterWeight: 0.7,
      throttle: 8,
      backgroundColor: "rgba(0,0,0,0)",
    });
    padRef.current = pad;
    if (value) {
      pad.fromDataURL(value, { ratio });
      setEmpty(false);
    }
    pad.addEventListener("endStroke", () => {
      setEmpty(false);
      onChange(pad.toDataURL("image/png"));
    });
    return () => pad.off();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    padRef.current?.clear();
    setEmpty(true);
    onChange("");
  };

  const undo = () => {
    const pad = padRef.current;
    if (!pad) return;
    const data = pad.toData();
    data.pop();
    pad.fromData(data);
    if (data.length === 0) { setEmpty(true); onChange(""); }
    else onChange(pad.toDataURL("image/png"));
  };

  return (
    <div data-testid="signature-pad">
      <div className="relative rounded-xl border-2 border-dashed border-[#D7CCC8] bg-white overflow-hidden">
        <canvas ref={canvasRef} style={{ height }}
          className="w-full touch-none cursor-crosshair block"
          data-testid="signature-canvas"
          role="img"
          aria-label="Signature drawing area. Use mouse or touch to draw your signature. Use the Clear button to start over or Undo stroke to remove the last stroke."
          tabIndex={0} />
        {empty && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-[#84675B]">Draw your signature here (mouse or touch)</span>}
        <div className="pointer-events-none absolute bottom-6 left-6 right-6 border-b border-[#E5DFD3]" />
      </div>
      <div className="mt-2 flex items-center gap-4">
        <button type="button" data-testid="signature-clear-btn" onClick={clear}
          className="flex items-center gap-1 text-xs font-semibold text-[#C62828] hover:underline"><Eraser size={12} /> Clear</button>
        <button type="button" data-testid="signature-undo-btn" onClick={undo}
          className="flex items-center gap-1 text-xs font-semibold text-[#7B6157] hover:underline"><Undo2 size={12} /> Undo stroke</button>
      </div>
    </div>
  );
}

/*
 * Universal on-screen Signature Audit Block.
 *
 * Renders the signer's visible mark (drawn image or typed name) and the full
 * tamper-evidence stamp (signature_id, signer name and role, timestamp,
 * IP address, document hash, ESIGN Act and HIPAA citations). This is the
 * single, canonical way to display a signed document's signature anywhere in
 * the app. Do not roll your own signature markup, use this component.
 *
 * Props:
 *  sig               object (either a full signature payload
 *                    {signature_id, name, image, signed_at, doc_hash,
 *                    signer_name, signer_type, ip}, or a document row with
 *                    root-level signature_image / signed_by / signed_at /
 *                    signature_id / signature)
 *  label             optional label above the signature. Defaults to
 *                    "Electronic Signature".
 *  cosigned_by       optional supervisor co-signer name
 *  cosigned_at       optional supervisor co-signer timestamp
 *  compact           optional. When true, shrinks the block to fit dense
 *                    tables and inline previews.
 */
export function SignatureStamp({ sig, label = "Electronic Signature", cosigned_by, cosigned_at, compact = false }) {
  if (!sig) return null;
  const inner = sig.signature || {};
  const image = sig.image || inner.image || sig.signature_image || "";
  const typedName = sig.name || inner.name || sig.signature_name || sig.signed_by || "";
  const sigId = sig.signature_id || inner.signature_id || "";
  const signedAt = sig.signed_at || inner.signed_at || "";
  const signerName = sig.signer_name || inner.signer_name || sig.signed_by || typedName;
  const signerType = sig.signer_type || inner.signer_type || "staff";
  const ip = sig.ip || inner.ip || "";
  const hash = sig.doc_hash || inner.doc_hash || "";
  if (!image && !typedName && !sigId) return null;
  const when = signedAt ? `${signedAt.replace("T", " ").slice(0, 16)} UTC` : "not recorded";
  return (
    <div data-testid="signature-stamp" data-signature-id={sigId} className="mt-3">
      <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-[#7B6157]">{label}</div>
      <div className="inline-block max-w-full">
        {image
          ? <img src={image} alt={`Signature of ${signerName || typedName || "signer"}`} className={`block object-contain ${compact ? "h-8" : "h-14"} max-w-[320px] border-b-[1.5px] border-[#6D4C41] pl-1 pr-8`} />
          : <div className={`inline-block border-b-[1.5px] border-[#6D4C41] pl-1 pr-6 pt-1.5 pb-1 text-[#3E2723] ${compact ? "text-lg" : "text-2xl"}`} style={{ fontFamily: "'Dancing Script', 'Segoe Script', cursive" }}>{typedName}</div>}
      </div>
      {sigId && (
        <div className="mt-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#2E7D32]">
            <span aria-hidden>✓</span> Verified E-Signature
          </span>
        </div>
      )}
      <div className={`mt-2 rounded-md border border-[#D9C7B2] border-l-4 border-l-[#4E342E] bg-[#FBF8F1] px-3.5 py-2.5 ${compact ? "text-[10px]" : "text-[10.5px]"} leading-relaxed text-[#4E342E]`}>
        <div className="mb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-[#3E2723]">Signature Audit Trail</div>
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
          <div><span className="font-semibold text-[#3E2723]">Signature ID</span><br /><span className="font-mono">{sigId || "not recorded"}</span></div>
          <div><span className="font-semibold text-[#3E2723]">Signed by</span><br />{signerName || "-"} <span className="text-[#7B6157]">({signerType})</span></div>
          <div><span className="font-semibold text-[#3E2723]">Timestamp</span><br />{when}</div>
          <div><span className="font-semibold text-[#3E2723]">IP address</span><br />{ip || "not recorded"}</div>
          {hash && (
            <div className="sm:col-span-2">
              <span className="font-semibold text-[#3E2723]">Document hash (SHA-256)</span><br />
              <span className="break-all font-mono text-[#5D4037]">{hash}</span>
            </div>
          )}
          {cosigned_by && (
            <div className="sm:col-span-2">
              <span className="font-semibold text-[#33691E]">Co-signed by</span> {cosigned_by}{cosigned_at ? ` on ${(cosigned_at || "").replace("T", " ").slice(0, 16)}` : ""}
            </div>
          )}
          <div className="sm:col-span-2 mt-1 border-t border-dashed border-[#D9C7B2] pt-1.5 text-[9.5px] italic text-[#7B6157]">
            Signed under the ESIGN Act (15 U.S.C. Chapter 96) and HIPAA (45 CFR 164.312). Any modification to the signed content invalidates the document hash.
          </div>
        </div>
      </div>
    </div>
  );
}
